// Visualizador do manual dentro do Hub (PO1-3738). O PDF vem do backend com login
// (/treinamento/tutoriais/:codigo/manual/arquivo), sem link do S3, e as páginas
// são desenhadas em canvas pelo pdf.js: sem botão de baixar ou imprimir, sem
// clique direito, Ctrl+S e Ctrl+P bloqueados enquanto o modal está aberto e
// marca-d'água com quem está vendo. Quem quiser muito ainda tira print da tela;
// o objetivo é só o manual não sair do sistema com um clique.
// Modal no padrão da prévia do card PNG do Faturamento (cabeçalho em div, fade={false}).
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.
import React, { useEffect, useRef, useState } from "react";
import { Modal, Spinner } from "reactstrap";

import { useAuth } from "../../../hooks/auth";
import { baixarManualPdf, mensagemErroTreinamento } from "../../../services/treinamentoApi";
import { abrirPdf } from "./carregarPdf";

import "./ManualModal.css";

export const ZOOM_MIN = 0.6;
export const ZOOM_MAX = 2;
const PASSO_ZOOM = 0.2;

const dois = (v) => (v < 10 ? `0${v}` : String(v));

// "Ana Exemplo · ana@empresa.com · 27/10/2026"
export function textoMarcaDagua(user, hoje) {
  const u = user || {};
  const nome = [u.name, u.surname].filter(Boolean).join(" ").trim();
  const contato = u.email || u.emailAddress || u.userName || u.username || "";
  const d = hoje || new Date();
  const data = `${dois(d.getDate())}/${dois(d.getMonth() + 1)}/${d.getFullYear()}`;
  return [nome, contato, data].filter(Boolean).join(" · ");
}

// Texto repetido na diagonal, bem claro, por cima da página já desenhada.
export function desenharMarca(ctx, largura, altura, texto, escala) {
  if (!ctx || !texto) return;
  const fonte = Math.round(13 * (escala || 1));
  ctx.save();
  // Cinza médio: aparece na página branca e na capa escura.
  ctx.globalAlpha = 0.16;
  ctx.fillStyle = "#8a8a99";
  ctx.font = `600 ${fonte}px sans-serif`;
  ctx.translate(largura / 2, altura / 2);
  ctx.rotate(-Math.PI / 6);
  const passoX = Math.max(fonte * texto.length * 0.6, 200) + fonte * 4;
  const passoY = fonte * 9;
  const raio = Math.sqrt(largura * largura + altura * altura);
  for (let y = -raio; y < raio; y += passoY) {
    for (let x = -raio; x < raio; x += passoX) {
      ctx.fillText(texto, x, y);
    }
  }
  ctx.restore();
}

export default function ManualModal({ isOpen, tutorial, onClose }) {
  const { user } = useAuth();
  const [estado, setEstado] = useState({ carregando: false, erro: null, paginas: 0 });
  const [zoom, setZoom] = useState(1);
  const docRef = useRef(null);
  const caixaRef = useRef(null);
  const codigo = tutorial ? tutorial.codigo : null;

  // Baixa o PDF e abre no pdf.js. Fechar ou trocar de manual descarta o documento.
  useEffect(() => {
    if (!isOpen || !codigo) return undefined;
    let cancelado = false;
    setEstado({ carregando: true, erro: null, paginas: 0 });
    setZoom(1);
    (async () => {
      try {
        const dados = await baixarManualPdf(codigo);
        const doc = await abrirPdf(dados);
        if (cancelado) {
          doc.destroy();
          return;
        }
        docRef.current = doc;
        setEstado({ carregando: false, erro: null, paginas: doc.numPages });
      } catch (e) {
        if (!cancelado) setEstado({ carregando: false, erro: mensagemErroTreinamento(e, "Não foi possível abrir o manual."), paginas: 0 });
      }
    })();
    return () => {
      cancelado = true;
      if (docRef.current) {
        docRef.current.destroy();
        docRef.current = null;
      }
    };
  }, [isOpen, codigo]);

  // Desenha as páginas na largura da caixa × zoom, com a marca-d'água.
  useEffect(() => {
    const doc = docRef.current;
    const caixa = caixaRef.current;
    if (!doc || !caixa || !estado.paginas) return undefined;
    let cancelado = false;
    const tarefas = [];
    const marca = textoMarcaDagua(user, new Date());
    const dpr = (typeof window !== "undefined" && window.devicePixelRatio) || 1;
    const largura = Math.max(320, (caixa.clientWidth || 800) - 32) * zoom;
    (async () => {
      for (let n = 1; n <= estado.paginas; n += 1) {
        if (cancelado) return;
        const pagina = await doc.getPage(n);
        if (cancelado) return;
        const base = pagina.getViewport({ scale: 1 });
        const viewport = pagina.getViewport({ scale: (largura / base.width) * dpr });
        const canvas = caixa.querySelector(`canvas[data-pagina="${n}"]`);
        if (!canvas) continue;
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = `${Math.floor(viewport.width / dpr)}px`;
        canvas.style.height = `${Math.floor(viewport.height / dpr)}px`;
        const ctx = canvas.getContext ? canvas.getContext("2d") : null;
        const tarefa = pagina.render({ canvasContext: ctx, viewport });
        tarefas.push(tarefa);
        await tarefa.promise;
        if (!cancelado) desenharMarca(ctx, canvas.width, canvas.height, marca, dpr);
      }
    })().catch(() => {
      // render cancelado pela troca de zoom ou pelo fechamento: nada a mostrar
    });
    return () => {
      cancelado = true;
      tarefas.forEach((t) => {
        if (t && typeof t.cancel === "function") t.cancel();
      });
    };
  }, [estado.paginas, zoom, user]);

  // Ctrl/Cmd+S e Ctrl/Cmd+P não salvam nem imprimem o manual.
  useEffect(() => {
    if (!isOpen || typeof document === "undefined") return undefined;
    const onKey = (e) => {
      const tecla = String(e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (tecla === "s" || tecla === "p")) e.preventDefault();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [isOpen]);

  const paginas = [];
  for (let n = 1; n <= estado.paginas; n += 1) paginas.push(n);
  const mudarZoom = (delta) => setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, Math.round((z + delta) * 10) / 10)));

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" modalClassName="modal-align-top manual-modal" fade={false}>
      <div className="manual-modal__cabecalho">
        <div style={{ minWidth: 0 }}>
          <div className="manual-modal__eyebrow">Manual</div>
          <h4 className="manual-modal__titulo">{tutorial ? tutorial.titulo : ""}</h4>
        </div>
        <div className="manual-modal__acoes">
          {estado.paginas ? (
            <>
              <button type="button" className="manual-modal__zoom" onClick={() => mudarZoom(-PASSO_ZOOM)} disabled={zoom <= ZOOM_MIN} aria-label="Diminuir">
                −
              </button>
              <span className="manual-modal__zoom-valor" data-testid="manual-zoom">{Math.round(zoom * 100)}%</span>
              <button type="button" className="manual-modal__zoom" onClick={() => mudarZoom(PASSO_ZOOM)} disabled={zoom >= ZOOM_MAX} aria-label="Aumentar">
                +
              </button>
            </>
          ) : null}
          <button type="button" className="manual-modal__fechar" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>
      </div>

      <div
        className="manual-modal__corpo"
        ref={caixaRef}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        data-testid="manual-modal"
      >
        {estado.carregando ? (
          <div className="manual-modal__estado">
            <Spinner color="info" /> <span>Abrindo o manual…</span>
          </div>
        ) : null}
        {estado.erro ? (
          <div className="manual-modal__estado">
            <strong>Erro:</strong> {estado.erro}
          </div>
        ) : null}
        {paginas.map((n) => (
          <canvas key={n} data-pagina={n} className="manual-modal__pagina" aria-label={`Página ${n} de ${estado.paginas}`} />
        ))}
      </div>
    </Modal>
  );
}
